/**
 * G2eGente - Treinamentos Online (App Mobile do Funcionário)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { treinamentosOnlineAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft,
  Loader2,
  GraduationCap,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Trophy
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pendente: { label: 'Pendente', color: 'bg-slate-100 text-slate-700', icon: Clock },
  em_andamento: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700', icon: Play },
  prova_liberada: { label: 'Prova Liberada', color: 'bg-amber-100 text-amber-700', icon: FileText },
  concluido: { label: 'Concluído', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  reprovado: { label: 'Reprovado', color: 'bg-red-100 text-red-700', icon: XCircle }
};

// Lista de Treinamentos
export default function EmployeeTreinamentos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ treinamentos: [], pendentes: [], concluidos: [], reprovados: [] });
  const [tab, setTab] = useState('pendentes');

  useEffect(() => {
    fetchTreinamentos();
  }, []);

  const fetchTreinamentos = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await treinamentosOnlineAPI.getAll();
      setData(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acesso restrito a funcionários');
      } else {
        toast.error('Erro ao carregar treinamentos');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const currentList = tab === 'pendentes' ? data.pendentes : 
                      tab === 'concluidos' ? data.concluidos : data.reprovados;

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="employee-treinamentos">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 pt-8">
        <div className="flex items-center justify-between mb-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fetchTreinamentos(true)}
            disabled={refreshing}
            className="text-white hover:bg-white/20"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <h1 className="text-xl font-bold">Meus Treinamentos</h1>
        <p className="text-purple-100 text-sm">Treinamentos online da empresa</p>
        
        <div className="flex gap-2 mt-3">
          <div className="bg-white/20 rounded-lg px-3 py-1">
            <span className="text-sm">{data.pendentes?.length || 0} pendentes</span>
          </div>
          <div className="bg-white/20 rounded-lg px-3 py-1">
            <span className="text-sm">{data.concluidos?.length || 0} concluídos</span>
          </div>
        </div>
      </header>

      <main className="p-4">
        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={tab === 'pendentes' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTab('pendentes')}
            className={tab === 'pendentes' ? 'bg-purple-600' : ''}
          >
            Pendentes ({data.pendentes?.length || 0})
          </Button>
          <Button
            variant={tab === 'concluidos' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTab('concluidos')}
            className={tab === 'concluidos' ? 'bg-green-600' : ''}
          >
            Concluídos ({data.concluidos?.length || 0})
          </Button>
          {data.reprovados?.length > 0 && (
            <Button
              variant={tab === 'reprovados' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTab('reprovados')}
              className={tab === 'reprovados' ? 'bg-red-600' : ''}
            >
              Reprovados ({data.reprovados?.length || 0})
            </Button>
          )}
        </div>

        {currentList.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {tab === 'pendentes' ? 'Nenhum treinamento pendente' : 
                 tab === 'concluidos' ? 'Nenhum treinamento concluído' : 'Nenhum treinamento reprovado'}
              </h3>
              <p className="text-slate-500 text-sm">
                {tab === 'pendentes' ? 'Seus treinamentos aparecerão aqui' : 
                 'Continue participando das turmas da empresa'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {currentList.map(treinamento => {
              const StatusIcon = STATUS_CONFIG[treinamento.status_treinamento]?.icon || Clock;
              return (
                <Card 
                  key={treinamento.turma_id}
                  className="border-slate-200 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => navigate(`/m/employee/treinamentos/${treinamento.turma_id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-900 truncate">
                            {treinamento.training_nome || treinamento.turma_nome}
                          </h4>
                        </div>
                        <p className="text-xs text-slate-500 mb-2">{treinamento.turma_nome}</p>
                        
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={STATUS_CONFIG[treinamento.status_treinamento]?.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {STATUS_CONFIG[treinamento.status_treinamento]?.label}
                          </Badge>
                          {treinamento.modalidade === 'online' && (
                            <Badge variant="outline" className="text-purple-600 border-purple-200">
                              Online
                            </Badge>
                          )}
                        </div>
                        
                        {treinamento.status_treinamento !== 'concluido' && treinamento.modalidade === 'online' && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                              <span>Progresso do vídeo</span>
                              <span>{treinamento.percentual_assistido}%</span>
                            </div>
                            <Progress value={treinamento.percentual_assistido} className="h-2" />
                          </div>
                        )}
                        
                        {treinamento.status_treinamento === 'concluido' && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-green-600">
                            <Trophy className="w-4 h-4" />
                            <span>Nota: {treinamento.nota_prova}%</span>
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

// Detalhe do Treinamento
export function EmployeeTreinamentoDetalhe() {
  const { turmaId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [treinamento, setTreinamento] = useState(null);
  const [fazendoProva, setFazendoProva] = useState(false);
  const [respostas, setRespostas] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);
  const progressIntervalRef = useRef(null);

  useEffect(() => {
    fetchTreinamento();
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [turmaId]);

  const fetchTreinamento = async () => {
    try {
      const res = await treinamentosOnlineAPI.getById(turmaId);
      setTreinamento(res.data);
      if (res.data.questoes) {
        setRespostas(new Array(res.data.questoes.length).fill(-1));
      }
      if (res.data.resultado) {
        setResultado(res.data.resultado);
      }
    } catch (error) {
      toast.error('Erro ao carregar treinamento');
      navigate('/m/employee/treinamentos');
    } finally {
      setLoading(false);
    }
  };

  // Extrair ID do vídeo do YouTube
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    return match ? match[1] : null;
  };

  // Atualizar progresso do vídeo
  const handleVideoProgress = async (percentual) => {
    try {
      await treinamentosOnlineAPI.updateProgresso(turmaId, percentual);
      setTreinamento(prev => ({
        ...prev,
        percentual_assistido: Math.max(prev.percentual_assistido, percentual),
        prova_liberada: percentual >= prev.percentual_minimo
      }));
    } catch (error) {
      console.error('Erro ao atualizar progresso:', error);
    }
  };

  // Simular progresso do vídeo (em produção, usar YouTube API)
  const handlePlayVideo = () => {
    // Simulação: incrementar 10% a cada 3 segundos
    let current = treinamento.percentual_assistido;
    progressIntervalRef.current = setInterval(() => {
      current = Math.min(100, current + 10);
      handleVideoProgress(current);
      if (current >= 100) {
        clearInterval(progressIntervalRef.current);
      }
    }, 3000);
    toast.info('Assista o vídeo para liberar a prova');
  };

  const handleSubmitProva = async () => {
    if (respostas.includes(-1)) {
      toast.error('Por favor, responda todas as questões');
      return;
    }

    setSubmitting(true);
    try {
      const res = await treinamentosOnlineAPI.submitProva(turmaId, respostas);
      setResultado(res.data);
      setFazendoProva(false);
      
      if (res.data.aprovado) {
        toast.success('Parabéns! Você foi aprovado!');
      } else {
        toast.error('Infelizmente você não atingiu a nota mínima');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar prova');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!treinamento) return null;

  const videoId = getYouTubeVideoId(treinamento.video_youtube_url);

  // Tela de Resultado
  if (resultado) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="treinamento-resultado">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="flex items-center gap-3 p-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/m/employee/treinamentos')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-semibold text-slate-900">Resultado</h1>
          </div>
        </header>

        <main className="p-4">
          <Card className={`border-2 ${resultado.aprovado ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
            <CardContent className="py-8 text-center">
              {resultado.aprovado ? (
                <>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trophy className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-green-700 mb-2">Aprovado!</h2>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-10 h-10 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-red-700 mb-2">Reprovado</h2>
                </>
              )}
              
              <p className="text-4xl font-bold text-slate-900 mb-1">{resultado.nota}%</p>
              <p className="text-slate-500 mb-4">
                {resultado.acertos} de {resultado.total_questoes} questões corretas
              </p>
              <p className="text-sm text-slate-500">Nota mínima: {treinamento.nota_minima}%</p>
              
              {resultado.aprovado && (
                <p className="mt-4 text-sm text-green-700 bg-green-100 p-3 rounded-lg">
                  Este treinamento foi registrado automaticamente no seu perfil de qualificação.
                </p>
              )}
            </CardContent>
          </Card>

          <Button 
            className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
            onClick={() => navigate('/m/employee/treinamentos')}
          >
            Voltar aos Treinamentos
          </Button>
        </main>
      </div>
    );
  }

  // Tela de Prova
  if (fazendoProva && treinamento.questoes) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="treinamento-prova">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="flex items-center gap-3 p-4">
            <Button variant="ghost" size="icon" onClick={() => setFazendoProva(false)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-semibold text-slate-900">Prova de Validação</h1>
              <p className="text-xs text-slate-500">{treinamento.training_nome}</p>
            </div>
          </div>
        </header>

        <main className="p-4 pb-24">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>Atenção:</strong> Você tem apenas 1 tentativa para realizar esta prova. 
              Nota mínima: {treinamento.nota_minima}%
            </div>
          </div>

          <div className="space-y-4">
            {treinamento.questoes.map((questao, qIndex) => (
              <Card key={qIndex} className="border-slate-200">
                <CardContent className="p-4">
                  <p className="font-medium text-slate-900 mb-3">
                    {qIndex + 1}. {questao.pergunta}
                  </p>
                  <div className="space-y-2">
                    {questao.alternativas.map((alt, aIndex) => (
                      <label 
                        key={aIndex}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          respostas[qIndex] === aIndex 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`questao-${qIndex}`}
                          checked={respostas[qIndex] === aIndex}
                          onChange={() => {
                            const newRespostas = [...respostas];
                            newRespostas[qIndex] = aIndex;
                            setRespostas(newRespostas);
                          }}
                          className="accent-purple-600"
                        />
                        <span className="text-sm">{alt}</span>
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </main>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
          <Button 
            className="w-full bg-purple-600 hover:bg-purple-700"
            onClick={handleSubmitProva}
            disabled={submitting || respostas.includes(-1)}
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Enviar Respostas
          </Button>
        </div>
      </div>
    );
  }

  // Tela de Detalhe/Vídeo
  return (
    <div className="min-h-screen bg-slate-50" data-testid="treinamento-detalhe">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="flex items-center gap-3 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/m/employee/treinamentos')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-semibold text-slate-900 truncate">{treinamento.training_nome}</h1>
            <p className="text-xs text-slate-500">{treinamento.turma_nome}</p>
          </div>
        </div>
      </header>

      <main className="p-4 pb-24">
        {/* Vídeo do YouTube */}
        {videoId && (
          <div className="relative aspect-video bg-black rounded-xl overflow-hidden mb-4">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?rel=0`}
              title={treinamento.training_nome}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={handlePlayVideo}
            />
          </div>
        )}

        {/* Informações */}
        <Card className="border-slate-200 mb-4">
          <CardContent className="p-4">
            <h3 className="font-semibold text-slate-900 mb-2">{treinamento.training_nome}</h3>
            {treinamento.training_descricao && (
              <p className="text-sm text-slate-600 mb-3">{treinamento.training_descricao}</p>
            )}
            
            <div className="flex flex-wrap gap-2 mb-3">
              {treinamento.training_categoria && (
                <Badge variant="outline">{treinamento.training_categoria}</Badge>
              )}
              {treinamento.instrutor && (
                <Badge variant="outline">Instrutor: {treinamento.instrutor}</Badge>
              )}
            </div>

            {treinamento.modalidade === 'online' && (
              <>
                <div className="flex items-center justify-between text-sm text-slate-500 mb-1">
                  <span>Progresso do vídeo</span>
                  <span>{treinamento.percentual_assistido}% / {treinamento.percentual_minimo}% mínimo</span>
                </div>
                <Progress value={treinamento.percentual_assistido} className="h-2" />
              </>
            )}
          </CardContent>
        </Card>

        {/* Prova */}
        {treinamento.prova_ativa && (
          <Card className={`border-2 ${treinamento.prova_liberada ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${treinamento.prova_liberada ? 'bg-amber-100' : 'bg-slate-100'}`}>
                  <FileText className={`w-5 h-5 ${treinamento.prova_liberada ? 'text-amber-600' : 'text-slate-400'}`} />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-slate-900">Prova de Validação</h4>
                  <p className="text-sm text-slate-500">
                    {treinamento.total_questoes} questões • Nota mínima: {treinamento.nota_minima}%
                  </p>
                  
                  {!treinamento.prova_liberada && (
                    <p className="text-xs text-slate-500 mt-2">
                      Assista pelo menos {treinamento.percentual_minimo}% do vídeo para liberar a prova
                    </p>
                  )}
                  
                  {treinamento.ja_fez_prova && (
                    <Badge className="mt-2 bg-slate-100 text-slate-600">
                      Prova já realizada
                    </Badge>
                  )}
                </div>
              </div>
              
              {treinamento.prova_liberada && !treinamento.ja_fez_prova && (
                <Button 
                  className="w-full mt-4 bg-amber-600 hover:bg-amber-700"
                  onClick={() => setFazendoProva(true)}
                >
                  Iniciar Prova
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
